import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Briefcase, 
  Building2,
  Edit2,
  Trash2,
  MessageSquare,
  CheckSquare,
  Plus,
  Brain
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { API_BASE_URL } from '../lib/api';

interface Deal {
  id: string;
  dealId: string;
  dealName: string;
  accountId: string;
  accountName: string;
  dealType: string;
  dealStatus: string;
  dealAmount: number;
  dealCost: number;
  dealProfit: number;
  dealOrderPlannedDate: string;
  dealOrderActualDate: string;
  dealStartDate: string;
  dealEndDate: string;
  aiWinProb: number;
  aiRiskFlags: string[];
  aiNextAction: string;
  remarks: string;
  createdAt: string;
  updatedAt: string;
}

interface Activity {
  id: string;
  activityTitle: string;
  activityDatetime: string;
}

interface Todo {
  id: string;
  todoTitle: string;
  todoStatus: string;
  todoDueDate: string;
}

export function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    fetchDeal();
  }, [id]);

  const fetchDeal = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/deals/${id}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('案件情報の取得に失敗しました');
      }
      
      const data = await response.json();
      setDeal(data.deal);
      setActivities(data.activities || []);
      setTodos(data.todos || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('この案件を削除しますか？')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/deals/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('削除に失敗しました');
      }
      
      navigate('/deals');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/deals/${id}/analyze`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('AI分析に失敗しました');
      }
      
      await fetchDeal();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      '見込み': 'bg-blue-100 text-blue-700',
      '提案中': 'bg-purple-100 text-purple-700',
      '交渉中': 'bg-yellow-100 text-yellow-700',
      '受注': 'bg-green-100 text-green-700',
      '失注': 'bg-red-100 text-red-700',
      '保留': 'bg-gray-100 text-gray-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { 
      style: 'currency', 
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ja-JP');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-red-500 mb-4">{error || '案件が見つかりません'}</p>
        <Button onClick={() => navigate('/deals')}>案件一覧に戻る</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/deals')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {deal.dealName}
              </h1>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(deal.dealStatus)}`}>
                {deal.dealStatus}
              </span>
            </div>
            <Link 
              to={`/accounts/${deal.accountId}`}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <Building2 size={14} />
              {deal.accountName}
            </Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            leftIcon={<Brain size={16} />}
            onClick={handleAnalyze}
            isLoading={isAnalyzing}
          >
            AI分析
          </Button>
          <Button 
            variant="outline" 
            leftIcon={<Edit2 size={16} />}
            onClick={() => navigate(`/deals/${id}/edit`)}
          >
            編集
          </Button>
          <Button 
            variant="danger" 
            leftIcon={<Trash2 size={16} />}
            onClick={handleDelete}
          >
            削除
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* メインコンテンツ */}
        <div className="lg:col-span-2 space-y-6">
          {/* 金額情報 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="text-center">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>売上金額</p>
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(deal.dealAmount)}
              </p>
            </Card>
            <Card className="text-center">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>原価</p>
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(deal.dealCost)}
              </p>
            </Card>
            <Card className="text-center">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>利益</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(deal.dealProfit)}
              </p>
            </Card>
          </div>

          {/* 案件詳細 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase size={20} />
                案件詳細
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>案件ID</p>
                  <p style={{ color: 'var(--text-primary)' }}>{deal.dealId}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>案件種別</p>
                  <p style={{ color: 'var(--text-primary)' }}>{deal.dealType || '-'}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>受注予定日</p>
                  <p style={{ color: 'var(--text-primary)' }}>{formatDate(deal.dealOrderPlannedDate)}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>受注日</p>
                  <p style={{ color: 'var(--text-primary)' }}>{formatDate(deal.dealOrderActualDate)}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>開始日</p>
                  <p style={{ color: 'var(--text-primary)' }}>{formatDate(deal.dealStartDate)}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>終了日</p>
                  <p style={{ color: 'var(--text-primary)' }}>{formatDate(deal.dealEndDate)}</p>
                </div>
              </div>

              {deal.remarks && (
                <>
                  <hr style={{ borderColor: 'var(--border-color)' }} />
                  <div>
                    <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>備考</p>
                    <p style={{ color: 'var(--text-primary)' }} className="whitespace-pre-wrap">
                      {deal.remarks}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* AI分析結果 */}
          {(deal.aiWinProb || deal.aiRiskFlags?.length || deal.aiNextAction) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain size={20} className="text-purple-600" />
                  AI分析
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {deal.aiWinProb !== undefined && deal.aiWinProb !== null && (
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>受注確度</p>
                      <p className={`text-3xl font-bold ${
                        deal.aiWinProb >= 70 ? 'text-green-500' :
                        deal.aiWinProb >= 40 ? 'text-yellow-500' : 'text-red-500'
                      }`}>
                        {deal.aiWinProb}%
                      </p>
                    </div>
                    <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          deal.aiWinProb >= 70 ? 'bg-green-500' :
                          deal.aiWinProb >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${deal.aiWinProb}%` }}
                      />
                    </div>
                  </div>
                )}

                {deal.aiRiskFlags && deal.aiRiskFlags.length > 0 && (
                  <div>
                    <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>リスクフラグ</p>
                    <div className="flex flex-wrap gap-2">
                      {deal.aiRiskFlags.map((flag, i) => (
                        <span 
                          key={i}
                          className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm"
                        >
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {deal.aiNextAction && (
                  <div>
                    <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>推奨アクション</p>
                    <p className="p-3 rounded-lg bg-blue-50 text-blue-700">
                      {deal.aiNextAction}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* サイドバー */}
        <div className="space-y-6">
          {/* 商談履歴 */}
          <Card padding="none">
            <CardHeader className="flex flex-row items-center justify-between p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare size={18} />
                商談履歴
              </CardTitle>
              <Link 
                to={`/activities/new?dealId=${id}`}
                className="text-blue-600 hover:text-blue-700"
              >
                <Plus size={18} />
              </Link>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-center py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  商談がありません
                </p>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {activities.map((activity) => (
                    <Link 
                      key={activity.id} 
                      to={`/activities/${activity.id}`}
                      className="block py-3 hover:bg-gray-50 -mx-4 px-4"
                    >
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {activity.activityTitle || '(タイトルなし)'}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(activity.activityDatetime)}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ToDo */}
          <Card padding="none">
            <CardHeader className="flex flex-row items-center justify-between p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckSquare size={18} />
                ToDo
              </CardTitle>
              <Link 
                to={`/todos?dealId=${id}`}
                className="text-blue-600 hover:text-blue-700"
              >
                <Plus size={18} />
              </Link>
            </CardHeader>
            <CardContent>
              {todos.length === 0 ? (
                <p className="text-center py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  ToDoがありません
                </p>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {todos.map((todo) => (
                    <div key={todo.id} className="py-3">
                      <p 
                        className={`font-medium ${todo.todoStatus === 'completed' ? 'line-through' : ''}`}
                        style={{ color: todo.todoStatus === 'completed' ? 'var(--text-secondary)' : 'var(--text-primary)' }}
                      >
                        {todo.todoTitle}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(todo.todoDueDate)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
